import { AxiosInstance, isAxiosError } from 'axios';
import { Currency } from 'dinero.js';
import type { components, operations } from '@/api/generated/openapi';
import { DataFetcherFunction } from '@/compiler/types';
import { axiosWithoutCaseMiddleware, fetchPaginatedDataFunctionWithMapper } from '@/helpers/forms/axios';
import {
    WfsAddress,
    WfsBoxLabelDownload,
    WfsBoxLabelOptions,
    WfsCarrierBooking,
    WfsCarrierBookingRequest,
    WfsCarrierLabelSize,
    WfsCarrierPackage,
    WfsCarrierTrackingEntry,
    WfsDocumentDownload,
    WfsDraftInboundOrderItem,
    WfsDraftInboundOrderRequest,
    WfsInboundOrder,
    WfsInboundOrderDetail,
    WfsInboundOrderItem,
    WfsInboundPreview,
    WfsItemHold,
    WfsItsFee,
    WfsItsFeeItem,
    WfsPickup,
    WfsPickupDate,
    WfsPickupRescheduleRequest,
    WfsPreviewDestination,
    WfsPreviewItem,
    WfsRateQuote,
    WfsRateQuoteRequest,
    WfsShipment,
    WfsShipmentItem,
    WfsShipFromAddress,
    WfsShipmentTrackingRequest,
    WfsTimelineDetails,
    WfsTimelineEntry,
} from '@/types/wfs';

type GeneratedWfsInboundOrderDetail = components['schemas']['WfsInboundOrderDetail'];

export type WfsAddressResponseRaw = components['schemas']['WfsAddress'];
export type WfsInboundOrderResponseRaw = components['schemas']['WfsInboundOrder'];
export type WfsInboundOrderItemResponseRaw = GeneratedWfsInboundOrderDetail['items'][number];
export type WfsShipmentResponseRaw = GeneratedWfsInboundOrderDetail['shipments'][number];
export type WfsShipmentItemResponseRaw = WfsShipmentResponseRaw['items'][number];
export type WfsCarrierBookingResponseRaw = NonNullable<WfsShipmentResponseRaw['carrier_booking']>;
export type WfsPickupResponseRaw = NonNullable<WfsCarrierBookingResponseRaw['pickup']>;

export type WfsInboundOrderDetailResponseRaw = GeneratedWfsInboundOrderDetail;
export type WfsTimelineEntryResponseRaw = GeneratedWfsInboundOrderDetail['timeline'][number];
export type WfsTimelineDetailsResponseRaw = NonNullable<GeneratedWfsInboundOrderDetail['timeline'][number]['details']>;
export type WfsTimelinePreviewDestinationResponseRaw = WfsTimelineDetailsResponseRaw['destinations'][number];

export type ShowWfsInboundOrderResponse = operations['show-wfs-inbound-order']['responses']['200']['content']['application/json'];
export type DraftWfsInboundOrderResponse = operations['draft-wfs-inbound-order']['responses']['201']['content']['application/json'];
export type UpdateWfsInboundOrderItemsResponse = operations['update-wfs-inbound-order-items']['responses']['200']['content']['application/json'];
export type SubmitWfsInboundOrderResponse = operations['submit-wfs-inbound-order']['responses']['202']['content']['application/json'];
export type RequestWfsInboundOrderCancellationResponse = operations['request-wfs-inbound-order-cancellation']['responses']['200']['content']['application/json'];
export type UpdateWfsShipmentQuantitiesResponse = operations['update-wfs-shipment-quantities']['responses']['200']['content']['application/json'];
export type SubmitWfsShipmentTrackingResponse = operations['submit-wfs-shipment-tracking']['responses']['200']['content']['application/json'];
export type BookWfsShipmentCarrierResponse = operations['book-wfs-shipment-carrier']['responses']['200']['content']['application/json'];
export type CancelWfsShipmentCarrierBookingResponse = operations['cancel-wfs-shipment-carrier-booking']['responses']['200']['content']['application/json'];
export type GenerateWfsShipmentCarrierLabelResponse = operations['generate-wfs-shipment-carrier-label']['responses']['202']['content']['application/json'];
export type RefreshWfsShipmentPickupResponse = operations['refresh-wfs-shipment-pickup']['responses']['200']['content']['application/json'];
export type RescheduleWfsShipmentPickupResponse = operations['reschedule-wfs-shipment-pickup']['responses']['200']['content']['application/json'];

export type PreviewWfsInboundOrderResponse = operations['preview-wfs-inbound-order']['responses']['200']['content']['application/json'];
export type IndexWfsItemHoldsResponse = operations['index-wfs-item-holds']['responses']['200']['content']['application/json'];
export type IndexWfsShipmentRateQuotesResponse = operations['index-wfs-shipment-rate-quotes']['responses']['200']['content']['application/json'];
export type RequestWfsShipmentRateQuotesResponse = operations['request-wfs-shipment-rate-quotes']['responses']['200']['content']['application/json'];
export type IndexWfsShipmentCarrierTrackingResponse = operations['index-wfs-shipment-carrier-tracking']['responses']['200']['content']['application/json'];

export type WfsRateQuoteResponseRaw = components['schemas']['WfsRateQuote'];
export type WfsCarrierTrackingEntryResponseRaw = IndexWfsShipmentCarrierTrackingResponse['data'][number];
export type WfsInboundPreviewResponseRaw = PreviewWfsInboundOrderResponse['data'];
export type WfsPreviewDestinationResponseRaw = WfsInboundPreviewResponseRaw['destinations'][number];
export type WfsPreviewItemResponseRaw = WfsPreviewDestinationResponseRaw['items'][number];
export type WfsItsFeeResponseRaw = WfsInboundPreviewResponseRaw['its_fees'][number];
export type WfsItsFeeItemResponseRaw = WfsItsFeeResponseRaw['items'][number];
export type WfsItemHoldResponseRaw = IndexWfsItemHoldsResponse['data'][number];

export type DraftWfsInboundOrderRequest = operations['draft-wfs-inbound-order']['requestBody']['content']['application/json'];
export type UpdateWfsInboundOrderItemsRequest = operations['update-wfs-inbound-order-items']['requestBody']['content']['application/json'];
export type PreviewWfsInboundOrderRequest = NonNullable<operations['preview-wfs-inbound-order']['requestBody']>['content']['application/json'];
export type UpdateWfsShipmentQuantitiesRequest = operations['update-wfs-shipment-quantities']['requestBody']['content']['application/json'];
export type SubmitWfsShipmentTrackingRequest = operations['submit-wfs-shipment-tracking']['requestBody']['content']['application/json'];
export type CreateWfsShipmentBoxLabelRequest = NonNullable<operations['create-wfs-shipment-box-label']['requestBody']>['content']['application/json'];
export type RequestWfsShipmentRateQuotesRequest = operations['request-wfs-shipment-rate-quotes']['requestBody']['content']['application/json'];
export type BookWfsShipmentCarrierRequest = operations['book-wfs-shipment-carrier']['requestBody']['content']['application/json'];
export type GenerateWfsShipmentCarrierLabelRequest = NonNullable<operations['generate-wfs-shipment-carrier-label']['requestBody']>['content']['application/json'];
export type RescheduleWfsShipmentPickupRequest = operations['reschedule-wfs-shipment-pickup']['requestBody']['content']['application/json'];

type WfsDraftInboundOrderItemRequest = DraftWfsInboundOrderRequest['items'][number];
type WfsShipFromRequest = RequestWfsShipmentRateQuotesRequest['ship_from'];
type WfsPackageRequest = NonNullable<RequestWfsShipmentRateQuotesRequest['packages']>[number];
type WfsPickupDateRequest = RescheduleWfsShipmentPickupRequest['pickup_dates'][number];

export function toWfsAddress(address: WfsAddressResponseRaw): WfsAddress {
    return {
        addressLine1: address.address_line_1,
        addressLine2: address.address_line_2,
        city: address.city,
        stateCode: address.state_code,
        countryCode: address.country_code,
        postalCode: address.postal_code,
    };
}

export function toWfsInboundOrder(order: WfsInboundOrderResponseRaw): WfsInboundOrder {
    return {
        uuid: order.uuid,
        inboundOrderId: order.inbound_order_id,
        channelId: order.channel_id,
        status: order.status,
        itemCount: order.item_count,
        unitCount: order.unit_count,
        shipmentIds: order.shipment_ids,
        errors: order.errors,
        consecutiveFailureCount: order.consecutive_failure_count,
        submittedAt: order.submitted_at,
        rejectedAt: order.rejected_at,
        cancellationRequestedAt: order.cancellation_requested_at,
        lastReconciledAt: order.last_reconciled_at,
        createdAt: order.created_at,
        updatedAt: order.updated_at,
    };
}

export function toWfsInboundOrderItem(item: WfsInboundOrderItemResponseRaw): WfsInboundOrderItem {
    return {
        sku: item.sku,
        gtin: item.gtin,
        itemDescription: item.item_description,
        itemQty: item.item_qty,
        vendorPackQty: item.vendor_pack_qty,
        innerPackQty: item.inner_pack_qty,
        expectedDeliveryDate: item.expected_delivery_date,
        itemLabeling: item.item_labeling,
        wfs: { enabled: item.wfs.enabled, syncedAt: item.wfs.synced_at },
    };
}

export function toWfsShipmentItem(item: WfsShipmentItemResponseRaw): WfsShipmentItem {
    return {
        sku: item.sku,
        gtin: item.gtin,
        requestedQty: item.requested_qty,
        receivedQty: item.received_qty,
        damagedQty: item.damaged_qty,
    };
}

export function toWfsPickup(pickup: WfsPickupResponseRaw): WfsPickup {
    return {
        confirmationNumber: pickup.confirmation_number,
        date: pickup.date,
        readyTime: pickup.ready_time,
        closeTime: pickup.close_time,
        status: pickup.status,
    };
}

export function toWfsCarrierBooking(booking: WfsCarrierBookingResponseRaw): WfsCarrierBooking {
    return {
        quoteType: booking.quote_type,
        quoteId: booking.quote_id,
        carrierId: booking.carrier_id,
        carrierName: booking.carrier_name,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves currency a free string, not an ISO 4217 code
        currency: booking.currency as Currency,
        netChargeCents: booking.net_charge_cents,
        state: booking.state,
        termsAcceptedAt: booking.terms_accepted_at,
        bookedAt: booking.booked_at,
        cancelledAt: booking.cancelled_at,
        invalidatedAt: booking.invalidated_at,
        shipDate: booking.ship_date,
        labelGeneratedAt: booking.label_generated_at,
        cancelBy: booking.cancel_by,
        pickup: booking.pickup ? toWfsPickup(booking.pickup) : null,
    };
}

export function toWfsShipment(shipment: WfsShipmentResponseRaw): WfsShipment {
    return {
        shipmentId: shipment.shipment_id,
        fcName: shipment.fc_name,
        shipToAddress: toWfsAddress(shipment.ship_to_address),
        status: shipment.status,
        expectedDeliveryDate: shipment.expected_delivery_date,
        updatedExpectedDeliveryDate: shipment.updated_expected_delivery_date,
        actualDeliveryDate: shipment.actual_delivery_date,
        requestedUnits: shipment.requested_units,
        receivedUnits: shipment.received_units,
        damagedUnits: shipment.damaged_units,
        carrierName: shipment.carrier_name,
        trackingNumbers: shipment.tracking_numbers,
        allocatedAt: shipment.allocated_at,
        items: shipment.items.map(toWfsShipmentItem),
        carrierBooking: shipment.carrier_booking ? toWfsCarrierBooking(shipment.carrier_booking) : null,
    };
}

export function toWfsTimelineDetails(details: WfsTimelineDetailsResponseRaw): WfsTimelineDetails {
    return {
        itemLabelingSkus: details.item_labeling_skus,
        destinations: details.destinations.map(destination => ({
            type: destination.type,
            walmartType: destination.walmart_type,
            shipNode: destination.ship_node,
            fcName: destination.fc_name,
            units: destination.units,
            netTransferChargeCents: destination.net_transfer_charge_cents,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves currency a free string, not an ISO 4217 code
            currency: destination.currency as Currency | null,
        })),
    };
}

export function toWfsTimelineEntry(entry: WfsTimelineEntryResponseRaw): WfsTimelineEntry {
    return {
        eventUuid: entry.event_uuid,
        eventType: entry.event_type,
        userUuid: entry.user_uuid,
        shipmentId: entry.shipment_id,
        details: entry.details ? toWfsTimelineDetails(entry.details) : null,
        occurredAt: entry.occurred_at,
    };
}

export function toWfsInboundOrderDetail(order: WfsInboundOrderDetailResponseRaw): WfsInboundOrderDetail {
    return {
        ...toWfsInboundOrder(order),
        returnAddress: toWfsAddress(order.return_address),
        inventoryTransferService: order.inventory_transfer_service,
        items: order.items.map(toWfsInboundOrderItem),
        shipments: order.shipments.map(toWfsShipment),
        timeline: order.timeline.map(toWfsTimelineEntry),
    };
}

export function toWfsPreviewItem(item: WfsPreviewItemResponseRaw): WfsPreviewItem {
    return {
        sku: item.sku,
        gtin: item.gtin,
        itemQty: item.item_qty,
    };
}

export function toWfsPreviewDestination(destination: WfsPreviewDestinationResponseRaw): WfsPreviewDestination {
    const address = destination.ship_to_address;

    return {
        type: destination.type,
        walmartType: destination.walmart_type,
        shipNode: destination.ship_node,
        nodeType: destination.node_type,
        isSortable: destination.is_sortable,
        isNonSortable: destination.is_non_sortable,
        shipToAddress: address === null
            ? null
            : {
                fcName: address.fc_name,
                addressLine1: address.address_line_1,
                addressLine2: address.address_line_2,
                city: address.city,
                stateCode: address.state_code,
                countryCode: address.country_code,
                postalCode: address.postal_code,
            },
        units: destination.units,
        items: destination.items.map(toWfsPreviewItem),
    };
}

export function toWfsItsFeeItem(item: WfsItsFeeItemResponseRaw): WfsItsFeeItem {
    return {
        sku: item.sku,
        gtin: item.gtin,
        itemQty: item.item_qty,
        netTransferChargeCents: item.net_transfer_charge_cents,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves currency a free string, not an ISO 4217 code
        currency: item.currency as Currency | null,
    };
}

export function toWfsItsFee(fee: WfsItsFeeResponseRaw): WfsItsFee {
    return {
        shipNode: fee.ship_node,
        fcName: fee.fc_name,
        netTransferChargeCents: fee.net_transfer_charge_cents,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves currency a free string, not an ISO 4217 code
        currency: fee.currency as Currency | null,
        items: fee.items.map(toWfsItsFeeItem),
    };
}

export function toWfsInboundPreview(preview: WfsInboundPreviewResponseRaw): WfsInboundPreview {
    return {
        destinations: preview.destinations.map(toWfsPreviewDestination),
        itsFees: preview.its_fees.map(toWfsItsFee),
    };
}

export function toWfsItemHold(hold: WfsItemHoldResponseRaw): WfsItemHold {
    return {
        gtin: hold.gtin,
        sku: hold.sku,
        status: hold.status,
        field: hold.field,
        description: hold.description,
        updatedAt: hold.updated_at,
    };
}

export function toWfsRateQuote(quote: WfsRateQuoteResponseRaw): WfsRateQuote {
    return {
        uuid: quote.uuid,
        quoteType: quote.quote_type,
        quoteId: quote.quote_id,
        carrierId: quote.carrier_id,
        carrierName: quote.carrier_name,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- the spec leaves currency a free string, not an ISO 4217 code
        currency: quote.currency as Currency,
        netChargeCents: quote.net_charge_cents,
        totalChargeCents: quote.total_charge_cents,
        estimatedDeliveryAt: quote.estimated_delivery_at,
        expiresAt: quote.expires_at,
        quoteStatus: quote.quote_status,
        requestedAt: quote.requested_at,
        selectedAt: quote.selected_at,
    };
}

export function toWfsCarrierTrackingEntry(entry: WfsCarrierTrackingEntryResponseRaw): WfsCarrierTrackingEntry {
    return {
        shipmentId: entry.shipment_id,
        trackingNumber: entry.tracking_no,
        status: entry.status,
        carrierName: entry.carrier_name,
        isMaster: entry.is_master,
        currentLocation: entry.current_location,
        estimatedDeliveryAt: entry.estimated_delivery_at,
        actualDeliveryAt: entry.actual_delivery_at,
        proNumber: entry.pro_number,
        eventAt: entry.event_at,
        mode: entry.mode,
        eventTimeZone: entry.event_time_zone,
    };
}

export function walmartErrorsFrom(error: unknown): string[] {
    if (!isAxiosError(error) || error.response?.status !== 422) return [];

    const body: unknown = error.response.data;
    if (typeof body !== 'object' || body === null || !('errors' in body)) return [];

    const errors: unknown = body.errors;
    if (typeof errors !== 'object' || errors === null || !('walmart' in errors) || !Array.isArray(errors.walmart)) return [];

    return errors.walmart.filter((message: unknown): message is string => typeof message === 'string');
}

const itemErrorKeyPattern = /^items\.(\d+)(?:\.|$)/;

// Maps a 422's `items.N.field` and whole-row `items.N` validation errors to the row they belong to.
export function itemValidationErrorsFrom(error: unknown): Record<number, string[]> {
    if (!isAxiosError(error) || error.response?.status !== 422) return {};

    const body: unknown = error.response.data;
    if (typeof body !== 'object' || body === null || !('errors' in body)) return {};

    const errors: unknown = body.errors;
    if (typeof errors !== 'object' || errors === null) return {};

    const rowErrors: Record<number, string[]> = {};
    Object.entries(errors).forEach(([key, messages]: [string, unknown]) => {
        const match = itemErrorKeyPattern.exec(key);
        if (match?.[1] == null || !Array.isArray(messages)) return;

        const row = Number(match[1]);
        rowErrors[row] = [
            ...(rowErrors[row] ?? []),
            ...messages.filter((message: unknown): message is string => typeof message === 'string'),
        ];
    });

    return rowErrors;
}

const attachmentFilename = (contentDisposition: unknown, fallback: string): string => {
    if (typeof contentDisposition === 'string') {
        const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
        if (match?.[1] != null) return match[1];
    }

    return fallback;
};

const draftItemBody = (item: WfsDraftInboundOrderItem): WfsDraftInboundOrderItemRequest => ({
    sku: item.sku,
    gtin: item.gtin,
    item_description: item.itemDescription,
    item_qty: item.itemQty,
    vendor_pack_qty: item.vendorPackQty,
    inner_pack_qty: item.innerPackQty,
    expected_delivery_date: item.expectedDeliveryDate,
    item_labeling: item.itemLabeling,
});

const shipFromBody = (address: WfsShipFromAddress): WfsShipFromRequest => ({
    address_line_1: address.addressLine1,
    address_line_2: address.addressLine2,
    city: address.city,
    state_code: address.stateCode,
    country_code: address.countryCode,
    postal_code: address.postalCode,
    phone: address.phone,
    location_name: address.locationName,
});

const packageBody = (carrierPackage: WfsCarrierPackage): WfsPackageRequest => ({
    sequence_number: carrierPackage.sequenceNumber,
    weight_lb: carrierPackage.weightPounds,
    length_in: carrierPackage.lengthInches,
    width_in: carrierPackage.widthInches,
    height_in: carrierPackage.heightInches,
    package_type: carrierPackage.packageType,
});

const pickupDateBody = (pickupDate: WfsPickupDate, index: number): WfsPickupDateRequest => ({
    date: pickupDate.date,
    ready_time: pickupDate.readyTime,
    close_time: pickupDate.closeTime,
    priority: index + 1,
});

const rateQuoteRequestBody = (request: WfsRateQuoteRequest): RequestWfsShipmentRateQuotesRequest => {
    const body: RequestWfsShipmentRateQuotesRequest = {
        quote_type: request.quoteType,
        ship_from: shipFromBody(request.shipFrom),
    };

    if (request.packages !== null) body.packages = request.packages.map(packageBody);

    if (request.freight !== null) {
        body.pickup_from = request.freight.pickupFrom;
        body.pickup_to = request.freight.pickupTo;
        body.freight = {
            freight_class: request.freight.freightClass,
            declared_value: request.freight.declaredValue,
            single_sku_boxes: request.freight.singleSkuBoxes,
            mixed_sku_boxes: request.freight.mixedSkuBoxes,
            total_pallet_count: request.freight.totalPalletCount,
        };
        body.pallets = request.freight.pallets.map(packageBody);

        if (request.freight.equipment !== null) {
            body.equipment = request.freight.equipment.map(equipment => ({
                length_ft: equipment.lengthFeet,
                protection_level: equipment.protectionLevel,
                type: equipment.type,
            }));
            body.mode = request.freight.mode;
            body.service_level = request.freight.serviceLevel;
        }
    }

    return body;
};

const carrierBookingRequestBody = (request: WfsCarrierBookingRequest): BookWfsShipmentCarrierRequest => {
    const body: BookWfsShipmentCarrierRequest = {
        rate_quote_uuid: request.rateQuoteUuid,
        single_sku_boxes: request.singleSkuBoxes,
        mixed_sku_boxes: request.mixedSkuBoxes,
    };

    if (request.freight === null) return body;

    const freight = request.freight;

    return {
        ...body,
        business_location_name: freight.businessLocationName,
        shipper: {
            name: `${freight.shipper.firstName} ${freight.shipper.lastName}`,
            first_name: freight.shipper.firstName,
            last_name: freight.shipper.lastName,
            phone: freight.shipper.phone,
            email: freight.shipper.email,
        },
        ship_from: shipFromBody(freight.shipFrom),
        pallets: freight.pallets.map(packageBody),
        total_pallet_count: freight.totalPalletCount,
        pickup_instruction: freight.pickupInstruction,
        pickup_dates: freight.pickupDates?.map(pickupDateBody) ?? null,
        operation_hours: freight.operationHours.map(operationHours => ({
            day_of_week_code: operationHours.dayOfWeekCode,
            open_time: operationHours.openTime,
            close_time: operationHours.closeTime,
        })),
        pickup_window: freight.pickupWindow,
        schedule: freight.schedule === null
            ? null
            : {
                min_pickup_at: freight.schedule.minPickupAt,
                max_pickup_at: freight.schedule.maxPickupAt,
                min_due_at: freight.schedule.minDueAt,
                max_due_at: freight.schedule.maxDueAt,
            },
        equipment_code: freight.equipmentCode,
        equipment_type: freight.equipmentType,
        equipment_length_ft: freight.equipmentLengthFeet,
        protection_level: freight.protectionLevel,
    };
};

interface WfsApiReturn {
    fetchInboundOrdersForChannel: (channelId: number, preRequest?: (uri: URL) => URL) => DataFetcherFunction<WfsInboundOrder>
    showInboundOrder: (uuid: string) => Promise<WfsInboundOrderDetail>
    draftInboundOrder: (channelId: number, body: WfsDraftInboundOrderRequest) => Promise<WfsInboundOrderDetail>
    updateInboundOrderItems: (uuid: string, items: WfsDraftInboundOrderItem[], inventoryTransferService: boolean) => Promise<WfsInboundOrderDetail>
    previewInboundOrder: (uuid: string, body: { itemLabelingSkus?: string[] }) => Promise<WfsInboundPreview>
    fetchItemHolds: (channelId: number, gtin: string) => Promise<WfsItemHold[]>
    submitInboundOrder: (uuid: string) => Promise<WfsInboundOrderDetail>
    requestCancellation: (uuid: string) => Promise<WfsInboundOrderDetail>
    updateShipmentQuantities: (uuid: string, shipmentId: string, quantities: Record<string, number>) => Promise<WfsInboundOrderDetail>
    submitShipmentTracking: (uuid: string, shipmentId: string, tracking: WfsShipmentTrackingRequest) => Promise<WfsInboundOrderDetail>
    downloadBoxLabel: (uuid: string, shipmentId: string, options: WfsBoxLabelOptions) => Promise<WfsBoxLabelDownload>
    fetchRateQuotes: (uuid: string, shipmentId: string) => Promise<WfsRateQuote[]>
    requestRateQuotes: (uuid: string, shipmentId: string, body: WfsRateQuoteRequest) => Promise<WfsRateQuote[]>
    bookCarrier: (uuid: string, shipmentId: string, body: WfsCarrierBookingRequest) => Promise<WfsInboundOrderDetail>
    cancelCarrierBooking: (uuid: string, shipmentId: string) => Promise<WfsInboundOrderDetail>
    generateCarrierLabel: (uuid: string, shipmentId: string, shipDate?: string) => Promise<WfsInboundOrderDetail>
    downloadCarrierLabel: (uuid: string, shipmentId: string, labelSize: WfsCarrierLabelSize) => Promise<WfsDocumentDownload | 'pending'>
    downloadBillOfLading: (uuid: string, shipmentId: string) => Promise<WfsDocumentDownload>
    fetchCarrierTracking: (uuid: string, shipmentId: string) => Promise<WfsCarrierTrackingEntry[]>
    refreshPickup: (uuid: string, shipmentId: string) => Promise<WfsInboundOrderDetail>
    reschedulePickup: (uuid: string, shipmentId: string, body: WfsPickupRescheduleRequest) => Promise<WfsInboundOrderDetail>
}

const shipmentPath = (uuid: string, shipmentId: string): string => `/api/channels/walmart/wfs/inbound-orders/${uuid}/shipments/${shipmentId}`;

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): WfsApiReturn => ({
    fetchInboundOrdersForChannel: (channelId: number, preRequest?: (uri: URL) => URL): DataFetcherFunction<WfsInboundOrder> => {
        return fetchPaginatedDataFunctionWithMapper<WfsInboundOrderResponseRaw, WfsInboundOrder>(
            `/api/channels/walmart/${channelId}/wfs/inbound-orders`,
            toWfsInboundOrder,
            preRequest,
            axios,
        );
    },

    showInboundOrder: async (uuid: string): Promise<WfsInboundOrderDetail> => {
        const response = await axios.get<ShowWfsInboundOrderResponse>(`/api/channels/walmart/wfs/inbound-orders/${uuid}`);

        return toWfsInboundOrderDetail(response.data.data);
    },

    draftInboundOrder: async (channelId: number, body: WfsDraftInboundOrderRequest): Promise<WfsInboundOrderDetail> => {
        const requestBody: DraftWfsInboundOrderRequest = {
            inbound_order_id: body.inboundOrderId,
            inventory_transfer_service: body.inventoryTransferService,
            return_address: {
                address_line_1: body.returnAddress.addressLine1,
                address_line_2: body.returnAddress.addressLine2,
                city: body.returnAddress.city,
                state_code: body.returnAddress.stateCode,
                country_code: body.returnAddress.countryCode,
                postal_code: body.returnAddress.postalCode,
            },
            items: body.items.map(draftItemBody),
        };
        const response = await axios.post<DraftWfsInboundOrderResponse>(`/api/channels/walmart/${channelId}/wfs/inbound-orders`, requestBody);

        return toWfsInboundOrderDetail(response.data.data);
    },

    updateInboundOrderItems: async (uuid: string, items: WfsDraftInboundOrderItem[], inventoryTransferService: boolean): Promise<WfsInboundOrderDetail> => {
        const body: UpdateWfsInboundOrderItemsRequest = {
            inventory_transfer_service: inventoryTransferService,
            items: items.map(draftItemBody),
        };
        const response = await axios.put<UpdateWfsInboundOrderItemsResponse>(`/api/channels/walmart/wfs/inbound-orders/${uuid}/items`, body);

        return toWfsInboundOrderDetail(response.data.data);
    },

    previewInboundOrder: async (uuid: string, body: { itemLabelingSkus?: string[] }): Promise<WfsInboundPreview> => {
        const requestBody: PreviewWfsInboundOrderRequest = body.itemLabelingSkus === undefined ? {} : { item_labeling_skus: body.itemLabelingSkus };
        const response = await axios.post<PreviewWfsInboundOrderResponse>(`/api/channels/walmart/wfs/inbound-orders/${uuid}/preview`, requestBody);

        return toWfsInboundPreview(response.data.data);
    },

    fetchItemHolds: async (channelId: number, gtin: string): Promise<WfsItemHold[]> => {
        const response = await axios.get<IndexWfsItemHoldsResponse>(`/api/channels/walmart/${channelId}/wfs/holds`, { params: { gtin } });

        return response.data.data.map(toWfsItemHold);
    },

    submitInboundOrder: async (uuid: string): Promise<WfsInboundOrderDetail> => {
        const response = await axios.post<SubmitWfsInboundOrderResponse>(`/api/channels/walmart/wfs/inbound-orders/${uuid}/submit`);

        return toWfsInboundOrderDetail(response.data.data);
    },

    requestCancellation: async (uuid: string): Promise<WfsInboundOrderDetail> => {
        const response = await axios.post<RequestWfsInboundOrderCancellationResponse>(`/api/channels/walmart/wfs/inbound-orders/${uuid}/cancellation-request`);

        return toWfsInboundOrderDetail(response.data.data);
    },

    updateShipmentQuantities: async (uuid: string, shipmentId: string, quantities: Record<string, number>): Promise<WfsInboundOrderDetail> => {
        const body: UpdateWfsShipmentQuantitiesRequest = { quantities };
        const response = await axios.put<UpdateWfsShipmentQuantitiesResponse>(
            `/api/channels/walmart/wfs/inbound-orders/${uuid}/shipments/${shipmentId}/quantities`,
            body,
        );

        return toWfsInboundOrderDetail(response.data.data);
    },

    submitShipmentTracking: async (uuid: string, shipmentId: string, tracking: WfsShipmentTrackingRequest): Promise<WfsInboundOrderDetail> => {
        const body: SubmitWfsShipmentTrackingRequest = { carrier_name: tracking.carrierName, tracking_numbers: tracking.trackingNumbers };
        const response = await axios.post<SubmitWfsShipmentTrackingResponse>(
            `/api/channels/walmart/wfs/inbound-orders/${uuid}/shipments/${shipmentId}/tracking`,
            body,
        );

        return toWfsInboundOrderDetail(response.data.data);
    },

    downloadBoxLabel: async (uuid: string, shipmentId: string, options: WfsBoxLabelOptions): Promise<WfsBoxLabelDownload> => {
        const body: CreateWfsShipmentBoxLabelRequest = {
            label_size: options.labelSize,
            label_format: options.labelFormat,
            load_types: options.loadTypes.length > 0
                ? options.loadTypes.map(loadType => ({ count: loadType.count, load_type: loadType.loadType }))
                : null,
        };
        const response = await axios.post<Blob>(
            `/api/channels/walmart/wfs/inbound-orders/${uuid}/shipments/${shipmentId}/box-label`,
            body,
            { responseType: 'blob' },
        );

        return {
            blob: response.data,
            filename: attachmentFilename(response.headers['content-disposition'], `wfs-box-label-${shipmentId}.pdf`),
        };
    },

    fetchRateQuotes: async (uuid: string, shipmentId: string): Promise<WfsRateQuote[]> => {
        const response = await axios.get<IndexWfsShipmentRateQuotesResponse>(`${shipmentPath(uuid, shipmentId)}/rate-quotes`);

        return response.data.data.map(toWfsRateQuote);
    },

    requestRateQuotes: async (uuid: string, shipmentId: string, body: WfsRateQuoteRequest): Promise<WfsRateQuote[]> => {
        const response = await axios.post<RequestWfsShipmentRateQuotesResponse>(`${shipmentPath(uuid, shipmentId)}/rate-quotes`, rateQuoteRequestBody(body));

        return response.data.data.map(toWfsRateQuote);
    },

    bookCarrier: async (uuid: string, shipmentId: string, body: WfsCarrierBookingRequest): Promise<WfsInboundOrderDetail> => {
        const response = await axios.post<BookWfsShipmentCarrierResponse>(
            `${shipmentPath(uuid, shipmentId)}/carrier-booking`,
            carrierBookingRequestBody(body),
        );

        return toWfsInboundOrderDetail(response.data.data);
    },

    cancelCarrierBooking: async (uuid: string, shipmentId: string): Promise<WfsInboundOrderDetail> => {
        const response = await axios.delete<CancelWfsShipmentCarrierBookingResponse>(`${shipmentPath(uuid, shipmentId)}/carrier-booking`);

        return toWfsInboundOrderDetail(response.data.data);
    },

    generateCarrierLabel: async (uuid: string, shipmentId: string, shipDate?: string): Promise<WfsInboundOrderDetail> => {
        const body: GenerateWfsShipmentCarrierLabelRequest = shipDate === undefined ? {} : { ship_date: shipDate };
        const response = await axios.post<GenerateWfsShipmentCarrierLabelResponse>(`${shipmentPath(uuid, shipmentId)}/carrier-label`, body);

        return toWfsInboundOrderDetail(response.data.data);
    },

    downloadCarrierLabel: async (uuid: string, shipmentId: string, labelSize: WfsCarrierLabelSize): Promise<WfsDocumentDownload | 'pending'> => {
        const response = await axios.get<Blob>(`${shipmentPath(uuid, shipmentId)}/carrier-label`, {
            params: { label_size: labelSize },
            responseType: 'blob',
        });

        if (response.status === 202) return 'pending';

        return {
            blob: response.data,
            filename: attachmentFilename(response.headers['content-disposition'], `wfs-carrier-label-${shipmentId}.pdf`),
        };
    },

    downloadBillOfLading: async (uuid: string, shipmentId: string): Promise<WfsDocumentDownload> => {
        const response = await axios.get<Blob>(`${shipmentPath(uuid, shipmentId)}/bill-of-lading`, { responseType: 'blob' });

        return {
            blob: response.data,
            filename: attachmentFilename(response.headers['content-disposition'], `wfs-bol-${shipmentId}.pdf`),
        };
    },

    fetchCarrierTracking: async (uuid: string, shipmentId: string): Promise<WfsCarrierTrackingEntry[]> => {
        const response = await axios.get<IndexWfsShipmentCarrierTrackingResponse>(`${shipmentPath(uuid, shipmentId)}/carrier-tracking`);

        return response.data.data.map(toWfsCarrierTrackingEntry);
    },

    refreshPickup: async (uuid: string, shipmentId: string): Promise<WfsInboundOrderDetail> => {
        const response = await axios.post<RefreshWfsShipmentPickupResponse>(`${shipmentPath(uuid, shipmentId)}/pickup/refresh`);

        return toWfsInboundOrderDetail(response.data.data);
    },

    reschedulePickup: async (uuid: string, shipmentId: string, body: WfsPickupRescheduleRequest): Promise<WfsInboundOrderDetail> => {
        const requestBody: RescheduleWfsShipmentPickupRequest = {
            pickup_dates: body.pickupDates.map(pickupDateBody),
            contact: {
                first_name: body.contact.firstName,
                last_name: body.contact.lastName,
                email: body.contact.email,
                area_code: body.contact.areaCode,
                number: body.contact.number,
                extension: body.contact.extension,
            },
        };
        const response = await axios.put<RescheduleWfsShipmentPickupResponse>(`${shipmentPath(uuid, shipmentId)}/pickup`, requestBody);

        return toWfsInboundOrderDetail(response.data.data);
    },
});
